import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2 } from 'lucide-react';
import { useMentionableUsers } from '../hooks/useMentionableUsers';
import type { MentionUser } from '../api/commentsApi';

// Shared id scheme between this component's option rows and the caller's
// `aria-activedescendant` (set on the textarea it never leaves).
export const mentionOptionId = (index: number) => `mention-option-${index}`;
// Shared with the caller's `aria-controls` (combobox -> listbox pairing).
export const MENTION_LISTBOX_ID = 'mention-listbox';

interface MentionPickerProps {
  issueKey: string;
  onSelect: (user: MentionUser) => void;
  activeIndex: number;
  onResultsChange: (results: MentionUser[]) => void;
  query: string;
  onQueryChange: (query: string | null) => void;
}

/**
 * Search dropdown for mentioning Techsell colleagues, driven by the comment textarea's `@` trigger.
 * `activeIndex` and keyboard nav stay owned by the caller — the textarea never loses focus.
 */
const MentionPicker = ({ issueKey, onSelect, activeIndex, onResultsChange, query, onQueryChange }: MentionPickerProps) => {
  const { t } = useTranslation();
  const { results, loading, error, hasEnoughChars, minChars } = useMentionableUsers(issueKey, query);

  // Report the live result set upward (needed by the caller to resolve
  // Enter/arrow-key moves) whenever the actual data changes.
  useEffect(() => {
    onResultsChange(results);
  }, [results, onResultsChange]);

  // Escape closes the dropdown, wherever the caret currently is (the textarea
  // driving `query` is a sibling, not a descendant, so a window listener is
  // the only way to catch it while this panel is mounted).
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      onQueryChange(null);
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onQueryChange]);

  return (
    <div
      id={MENTION_LISTBOX_ID}
      role="listbox"
      className="max-h-56 min-h-[2.5rem] overflow-y-auto rounded-md border border-gray-200 bg-white shadow-sm"
    >
      {!hasEnoughChars && (
        <p className="px-2 py-2 text-sm text-muted-foreground">
          {t('Type at least {{count}} characters', { count: minChars })}
        </p>
      )}

      {/* Results (including stale-but-cached ones) always win over the spinner,
          so a background refetch never blanks a still-valid, already-rendered
          list (and the aria-activedescendant it points into) behind a spinner. */}
      {hasEnoughChars && results.length > 0 && (
        <ul className="flex flex-col">
          {results.map((user, index) => (
            <li key={user.accountId}>
              <button
                type="button"
                id={mentionOptionId(index)}
                role="option"
                aria-selected={index === activeIndex}
                tabIndex={-1}
                onClick={() => onSelect(user)}
                className={`flex w-full items-center gap-2 px-2 py-2 text-left text-sm transition-colors hover:bg-accent ${
                  index === activeIndex ? 'bg-accent' : ''
                }`}
              >
                <span className="truncate">{user.displayName}</span>
              </button>
            </li>
          ))}
        </ul>
      )}

      {hasEnoughChars && results.length === 0 && loading && (
        <div className="flex items-center gap-2 px-2 py-2 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" />
          {t('Searching…')}
        </div>
      )}

      {hasEnoughChars && results.length === 0 && !loading && error && (
        <p className="px-2 py-2 text-sm text-destructive">{error}</p>
      )}

      {hasEnoughChars && results.length === 0 && !loading && !error && (
        <p className="px-2 py-2 text-sm text-muted-foreground">{t('No colleague found')}</p>
      )}
    </div>
  );
};

export default MentionPicker;
